/**
 * Icon registry composes the various icon sources into a single registry
 *
 * For each KEY, there is a corresponding component in the registry from one of the icon sources
 *
 * Generally, keys should be given semantic names (eg. EDIT not 'pencil', DELETE not 'trash') and registered if:
 * - They are common such as edit, delete, refresh, search, etc. or would likely be used in multiple places
 * - They are "domain" icons such as devices, sensors, valves, etc.
 *
 * Not every icon needs to be registered. One offs are allowed. Use your judgement.
 * Guidelines for one offs:
 * - Icons that are only used in one place or feature
 * - Localized icons and/or utilties where semantics dont make sense to register
 *
 */

import {
    ArrowDownAZ,
    ArrowUpAZ,
    Bell,
    BellOff,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    CircleAlert,
    CircleCheck,
    CircleDot,
    CircleMinus,
    CirclePlus,
    CircleQuestionMark,
    CircleX,
    Cloud,
    CloudAlert,
    CloudDownload,
    CloudOff,
    Download,
    EllipsisVertical,
    ExternalLink,
    Eye,
    EyeOff,
    Hand,
    HardDrive,
    Hash,
    InfoIcon,
    ListPlus,
    type LucideIcon,
    Move,
    Pencil,
    PercentIcon,
    Plus,
    RefreshCw,
    Save,
    SearchIcon,
    SettingsIcon,
    ShieldCheck,
    Trash2,
    TriangleAlert,
    Undo2,
    UserPlus,
    XIcon,
} from "lucide-react";
import * as ComposedLucideIcons from "./composed-lucide-icons";
import type { InlineReactSVGComponent } from "./inline-react-svgs/inline-react-svg-icons";
import * as InlineReactSVGs from "./inline-react-svgs/inline-react-svg-icons";
import * as LucideLabIcons from "./lucide-lab-icons";
import type { CustomSVGIconComponent } from "./svgs/svg-icons";
import * as CustomSVGIcons from "./svgs/svg-icons";

export type ICON_KEY = keyof typeof ICON_KEYS;

export type IconComponent =
    | CustomSVGIconComponent
    | InlineReactSVGComponent
    | LucideIcon
    | ComposedLucideIcons.ComposedLucideIconComponent;

export const ICON_KEYS = {
    // Custom SVG icons
    BATTERY_UNKNOWN: "BATTERY_UNKNOWN",
    BATTERY_ERROR: "BATTERY_ERROR",
    BATTERY_LOW: "BATTERY_LOW",
    BATTERY_OK: "BATTERY_OK",
    BATTERY_GOOD: "BATTERY_GOOD",
    BATTERY_FULL: "BATTERY_FULL",
    SIGNAL_UNKNOWN: "SIGNAL_UNKNOWN",
    SIGNAL_ERROR: "SIGNAL_ERROR",
    SIGNAL_VERY_LOW: "SIGNAL_VERY_LOW",
    SIGNAL_LOW: "SIGNAL_LOW",
    SIGNAL_MEDIUM: "SIGNAL_MEDIUM",
    SIGNAL_HIGH: "SIGNAL_HIGH",
    SIGNAL_FULL: "SIGNAL_FULL",
    VALVE_CLOSED: "VALVE_CLOSED",
    VALVE_OPEN: "VALVE_OPEN",
    SCHEDULE: "SCHEDULE",
    VERIFIED: "VERIFIED",
    SENSOR: "SENSOR",
    VALVE: "VALVE",
    PUMP: "PUMP",

    // Inline React SVG icons
    ZONE: "ZONE",
    VARIETAL_AVATAR: "VARIETAL_AVATAR",
    MOISTURE_SENSOR: "MOISTURE_SENSOR",
    PRESSURE_TRANSDUCER: "PRESSURE_TRANSDUCER",
    DOUBLE_LEFT_ARROW: "DOUBLE_LEFT_ARROW",
    DOUBLE_RIGHT_ARROW: "DOUBLE_RIGHT_ARROW",
    LEFT_ARROW: "LEFT_ARROW",
    RIGHT_ARROW: "RIGHT_ARROW",
    WATERDROP: "WATERDROP",
    INFO_OUTLINE_BLUE: "INFO_OUTLINE_BLUE",
    SUCCESS_OUTLINE: "SUCCESS_OUTLINE",
    CROP: "CROP",
    TENSIOMETER: "TENSIOMETER",
    WATERMARK: "WATERMARK",
    DUAL_VALVE_SEED: "DUAL_VALVE_SEED",
    SINGLE_VALVE_SEED: "SINGLE_VALVE_SEED",
    SPROUT_BLOCK_VALVE: "SPROUT_BLOCK_VALVE",
    DRAGINO_EC: "DRAGINO_EC",
    DRAGINO_GATEWAY: "DRAGINO_GATEWAY",

    // Lucide icons
    SAVE: "SAVE",
    EDIT: "EDIT",
    BACK: "BACK",
    FORWARD: "FORWARD",
    /** An in-place fold's chevron: up while closed, turned to point down while open. */
    DISCLOSURE: "DISCLOSURE",
    CLOSE: "CLOSE",
    DELETE: "DELETE",
    UNDO: "UNDO",
    REPOSITION: "REPOSITION",
    REFRESH: "REFRESH",
    DOWNLOAD: "DOWNLOAD",
    SYNC_DEVICES: "SYNC_DEVICES",
    // A remote-api-synced device's at-a-glance state (currently just weather stations): connected and
    // syncing, paused, or misconfigured/unreachable — meant to stay consistent across every device
    // type that syncs through a third-party credential, not just this one.
    CLOUD_CONNECTED: "CLOUD_CONNECTED",
    CLOUD_PAUSED: "CLOUD_PAUSED",
    CLOUD_ALERT: "CLOUD_ALERT",
    WARNING: "WARNING",
    // What a zone is doing now: a run going, or a manual override holding it off its schedule
    RUNNING: "RUNNING",
    ALL_CLEAR: "ALL_CLEAR",
    MANUAL_OVERRIDE: "MANUAL_OVERRIDE",
    ERROR: "ERROR",
    INFO: "INFO",
    EXTERNAL_LINK: "EXTERNAL_LINK",
    UNKNOWN: "UNKNOWN",
    SEARCH: "SEARCH",
    NEVER_CONNECTED: "NEVER_CONNECTED",
    CONNECTED: "CONNECTED",
    DISCONNECTED: "DISCONNECTED",
    ADD_USER: "ADD_USER",
    ADD: "ADD",
    REMOVE: "REMOVE",
    PERCENT: "PERCENT",
    SORT_DOWN_AZ: "SORT_DOWN_AZ",
    SORT_UP_AZ: "SORT_UP_AZ",
    PLUS: "PLUS",
    SETTINGS: "SETTINGS",
    NOTIFICATION: "NOTIFICATION",
    NOTIFICATION_OFF: "NOTIFICATION_OFF",
    // A phone number that can get RCS messages
    RCS: "RCS",
    REVEAL: "REVEAL",
    REVEAL_OFF: "REVEAL_OFF",
    MORE_ACTIONS: "MORE_ACTIONS",
    COUNT: "COUNT",
    DEVICE: "DEVICE",
    ADD_DATA_STREAM: "ADD_DATA_STREAM",

    // Composed Lucide icons
    ADD_ORGANIZATION: "ADD_ORGANIZATION",
    /** Adds a layer under the ones there are, as in a soil profile. */
    ADD_LAYER_BELOW: "ADD_LAYER_BELOW",
    /** Opens the soil texture triangle to pick a texture from. */
    SOIL_TEXTURE_PICKER: "SOIL_TEXTURE_PICKER",

    // Lucide Labs
    CROSSHAIR: "CROSSHAIR",
} as const;

/**
 * Icon keys with some keys grouped by source
 */
export const iconRegistry: Record<ICON_KEY, IconComponent> = {
    // Custom SVG icons
    [ICON_KEYS.BATTERY_UNKNOWN]: CustomSVGIcons.BatteryUnknown,
    [ICON_KEYS.BATTERY_ERROR]: CustomSVGIcons.BatteryError,
    [ICON_KEYS.BATTERY_LOW]: CustomSVGIcons.BatteryLow,
    [ICON_KEYS.BATTERY_OK]: CustomSVGIcons.BatteryOk,
    [ICON_KEYS.BATTERY_GOOD]: CustomSVGIcons.BatteryGood,
    [ICON_KEYS.BATTERY_FULL]: CustomSVGIcons.BatteryFull,
    [ICON_KEYS.SIGNAL_UNKNOWN]: CustomSVGIcons.SignalUnknown,
    [ICON_KEYS.SIGNAL_ERROR]: CustomSVGIcons.SignalError,
    [ICON_KEYS.SIGNAL_VERY_LOW]: CustomSVGIcons.SignalVeryLow,
    [ICON_KEYS.SIGNAL_LOW]: CustomSVGIcons.SignalLow,
    [ICON_KEYS.SIGNAL_MEDIUM]: CustomSVGIcons.SignalMedium,
    [ICON_KEYS.SIGNAL_HIGH]: CustomSVGIcons.SignalHigh,
    [ICON_KEYS.SIGNAL_FULL]: CustomSVGIcons.SignalFull,
    [ICON_KEYS.VALVE_CLOSED]: CustomSVGIcons.ValveClosed,
    [ICON_KEYS.VALVE_OPEN]: CustomSVGIcons.ValveOpen,
    [ICON_KEYS.SCHEDULE]: CustomSVGIcons.Schedule,
    [ICON_KEYS.VERIFIED]: CustomSVGIcons.Verified,
    [ICON_KEYS.SENSOR]: CustomSVGIcons.Sensor,
    [ICON_KEYS.VALVE]: CustomSVGIcons.Valve,
    [ICON_KEYS.PUMP]: CustomSVGIcons.Pump,

    // Inline React SVG icons
    [ICON_KEYS.ZONE]: InlineReactSVGs.Zone,
    [ICON_KEYS.VARIETAL_AVATAR]: InlineReactSVGs.VarietalAvatar,
    [ICON_KEYS.MOISTURE_SENSOR]: InlineReactSVGs.MoistureSensor,
    [ICON_KEYS.PRESSURE_TRANSDUCER]: InlineReactSVGs.PressureTransducer,
    [ICON_KEYS.DOUBLE_LEFT_ARROW]: InlineReactSVGs.DoubleLeftArrow,
    [ICON_KEYS.DOUBLE_RIGHT_ARROW]: InlineReactSVGs.DoubleRightArrow,
    [ICON_KEYS.LEFT_ARROW]: InlineReactSVGs.LeftArrow,
    [ICON_KEYS.RIGHT_ARROW]: InlineReactSVGs.RightArrow,
    [ICON_KEYS.WATERDROP]: InlineReactSVGs.Waterdrop,
    [ICON_KEYS.INFO_OUTLINE_BLUE]: InlineReactSVGs.InfoOutlineBlue,
    [ICON_KEYS.SUCCESS_OUTLINE]: InlineReactSVGs.SuccessOutline,
    [ICON_KEYS.CROP]: InlineReactSVGs.Crop,
    [ICON_KEYS.TENSIOMETER]: InlineReactSVGs.Tensiometer,
    [ICON_KEYS.WATERMARK]: InlineReactSVGs.Watermark,
    [ICON_KEYS.DUAL_VALVE_SEED]: InlineReactSVGs.DualValveSeed,
    [ICON_KEYS.SINGLE_VALVE_SEED]: InlineReactSVGs.SingleValveSeed,
    [ICON_KEYS.SPROUT_BLOCK_VALVE]: InlineReactSVGs.SproutBlockValve,
    [ICON_KEYS.DRAGINO_EC]: InlineReactSVGs.DraginoEC,
    [ICON_KEYS.DRAGINO_GATEWAY]: InlineReactSVGs.DraginoGateway,

    // Lucide icons
    [ICON_KEYS.SAVE]: Save,
    [ICON_KEYS.EDIT]: Pencil,
    [ICON_KEYS.BACK]: ChevronLeft,
    [ICON_KEYS.FORWARD]: ChevronRight,
    [ICON_KEYS.DISCLOSURE]: ChevronDown,
    [ICON_KEYS.CLOSE]: XIcon,
    [ICON_KEYS.DELETE]: Trash2,
    [ICON_KEYS.UNDO]: Undo2,
    [ICON_KEYS.REPOSITION]: Move,
    [ICON_KEYS.REFRESH]: RefreshCw,
    [ICON_KEYS.DOWNLOAD]: Download,
    [ICON_KEYS.SYNC_DEVICES]: CloudDownload,
    [ICON_KEYS.CLOUD_CONNECTED]: Cloud,
    [ICON_KEYS.CLOUD_PAUSED]: CloudOff,
    [ICON_KEYS.CLOUD_ALERT]: CloudAlert,
    [ICON_KEYS.WARNING]: TriangleAlert,
    [ICON_KEYS.RUNNING]: CircleDot,
    [ICON_KEYS.ALL_CLEAR]: CircleCheck,
    [ICON_KEYS.MANUAL_OVERRIDE]: Hand,
    [ICON_KEYS.ERROR]: CircleAlert,
    [ICON_KEYS.INFO]: InfoIcon,
    [ICON_KEYS.EXTERNAL_LINK]: ExternalLink,
    [ICON_KEYS.UNKNOWN]: CircleQuestionMark,
    [ICON_KEYS.SEARCH]: SearchIcon,
    [ICON_KEYS.NEVER_CONNECTED]: CircleQuestionMark,
    [ICON_KEYS.CONNECTED]: CircleCheck,
    [ICON_KEYS.DISCONNECTED]: CircleX,
    [ICON_KEYS.ADD_USER]: UserPlus,
    [ICON_KEYS.ADD]: CirclePlus,
    [ICON_KEYS.REMOVE]: CircleMinus,
    [ICON_KEYS.PERCENT]: PercentIcon,
    [ICON_KEYS.SORT_DOWN_AZ]: ArrowDownAZ,
    [ICON_KEYS.SORT_UP_AZ]: ArrowUpAZ,
    [ICON_KEYS.PLUS]: Plus,
    [ICON_KEYS.SETTINGS]: SettingsIcon,
    [ICON_KEYS.NOTIFICATION]: Bell,
    [ICON_KEYS.NOTIFICATION_OFF]: BellOff,
    [ICON_KEYS.RCS]: ShieldCheck,
    [ICON_KEYS.REVEAL]: Eye,
    [ICON_KEYS.REVEAL_OFF]: EyeOff,
    [ICON_KEYS.MORE_ACTIONS]: EllipsisVertical,
    [ICON_KEYS.COUNT]: Hash,
    [ICON_KEYS.DEVICE]: HardDrive,
    [ICON_KEYS.ADD_DATA_STREAM]: ListPlus,
    // Composed Lucide icons
    [ICON_KEYS.ADD_ORGANIZATION]: ComposedLucideIcons.AddOrganizationIcon,
    [ICON_KEYS.ADD_LAYER_BELOW]: ComposedLucideIcons.AddLayerBelowIcon,
    [ICON_KEYS.SOIL_TEXTURE_PICKER]: ComposedLucideIcons.SoilTexturePickerIcon,

    // Composed Lucide icons
    [ICON_KEYS.CROSSHAIR]: LucideLabIcons.Crosshair,
} as const;
